// Impossible Location (Audit tab and the node page's Location Check card). Pure helpers: labels, tags and
// number formatting. The backend decides every result; nothing here judges a position.

export type LocationLight = "red" | "yellow" | "green" | null;
export type LocationStatus = "impossible" | "suspected" | "possible" | "ok" | "not_enough_links";
export type LocationLinkResult = "too_long" | "terrain_blocked" | "pass" | "distance_only";

export interface LocationLimits {
  max_link_km: number;
  terrain_limit_m: number | null;
  calibration_links: number;
}

export interface LocationRow {
  key: string;
  name: string;
  role: string;
  status: LocationStatus;
  light: LocationLight;
  links_checked: number;
  links_failed: number;
  worst_link_km: number | null;
  clearance_needed_m: number | null;
  estimate_offset_km: number | null;
  elevation_m: number | null;
  sign_flip: boolean;
  /** Why the node is not green, most important first: null_island, sign_flip, too_long, terrain_blocked, offset. */
  reasons?: string[];
  lat?: number;
  lon?: number;
}

export interface LocationTable {
  protocol: string;
  window: string;
  generated_at: string;
  limits: LocationLimits;
  terrain_tiles: number;
  rows: LocationRow[];
}

export interface LocationLinkItem {
  key: string;
  name: string;
  km: number;
  clearance_m: number | null;
  result: LocationLinkResult;
  partner_impossible?: boolean;
}

export interface LocationCard {
  node: string;
  status: LocationStatus;
  light: LocationLight;
  links_checked: number;
  links_failed: number;
  elevation_m?: number | null;
  estimate_offset_km?: number | null;
  sign_flip?: boolean;
  reasons?: string[];
  lat?: number;
  lon?: number;
  links: LocationLinkItem[];
  limits: LocationLimits;
}

export interface TerrainStatus {
  state: "idle" | "running" | "done" | "error";
  message: string;
  tiles_planned: number;
  tiles_done: number;
  downloaded: number;
  present: number;
  no_data: number;
  failed: number;
  tiles_on_disk: number;
}

export const LOCATION_TAB_LABEL = "Impossible Location";
export const LOCATION_CARD_TITLE = "Location Check";
export const LOCATION_NO_VALUE = "-";
export const LOCATION_SETTINGS_PATH = "Settings > Audit";
export const LOCATION_EMPTY_TEXT = "No Direct Links Checked";

const LIGHT_TAG: Record<string, string> = { red: "Red", yellow: "Yellow", green: "Green" };
const STATUS_LABEL: Record<LocationStatus, string> = {
  impossible: "Impossible",
  suspected: "Suspected",
  possible: "Possible",
  ok: "OK",
  not_enough_links: "Not Enough Links",
};
const RESULT_LABEL: Record<LocationLinkResult, string> = {
  too_long: "Too Long",
  terrain_blocked: "Terrain Blocked",
  pass: "Pass",
  distance_only: "Distance Only",
};

const REASON_LABEL: Record<string, string> = {
  null_island: "Null Island",
  too_long: "Too Long",
  terrain_blocked: "Terrain Blocked",
  offset: "Offset",
  sign_flip: "Sign Flip",
};

/** Short tags for why a node is red or yellow; empty for a node with no reason. */
export function locationReasonTags(row: { reasons?: string[] }): string[] {
  return (row.reasons ?? []).map((r) => REASON_LABEL[r]).filter((t): t is string => Boolean(t));
}

/** A link that did not fail (a pass, or judged on distance alone). */
export function locationLinkPassed(result: LocationLinkResult): boolean {
  return result === "pass" || result === "distance_only";
}

export function locationLightTag(light: LocationLight): string {
  return light ? LIGHT_TAG[light] : "Not Enough Links";
}

export function locationStatusLabel(status: LocationStatus): string {
  return STATUS_LABEL[status] ?? status;
}

export function locationResultLabel(result: LocationLinkResult, partnerImpossible = false): string {
  const label = RESULT_LABEL[result] ?? result;
  return partnerImpossible && (result === "too_long" || result === "terrain_blocked") ? `${label} (Partner Impossible)` : label;
}

export function locationNumber(v: number | null | undefined, digits = 0): string {
  if (v === null || v === undefined || Number.isNaN(v)) return LOCATION_NO_VALUE;
  return v.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** Counts for a node with too few links read as dashes, as in the table mock. */
export function locationFailedText(row: { status: LocationStatus; links_failed: number }): string {
  return row.status === "not_enough_links" ? LOCATION_NO_VALUE : String(row.links_failed);
}

export function locationHeadline(card: { links_failed: number; links_checked: number }): string {
  return `Links Failed ${card.links_failed} of ${card.links_checked}`;
}

export function locationLimitsLine(limits: LocationLimits): string {
  return `Max Link ${locationNumber(limits.max_link_km)} km   Terrain Limit ${limits.terrain_limit_m === null ? LOCATION_NO_VALUE : `${locationNumber(limits.terrain_limit_m)} m`}`;
}

export function locationReported(lat: number | undefined, lon: number | undefined): string {
  if (lat === undefined || lon === undefined) return LOCATION_NO_VALUE;
  return `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
}

export function locationTerrainText(status: TerrainStatus | null, tilesInSnapshot: number): string {
  if (status && status.state === "running") return `Downloading ${status.tiles_done} Of ${status.tiles_planned} Tiles`;
  if (status && status.state === "error" && status.message) return status.message;
  const n = status ? status.tiles_on_disk : tilesInSnapshot;
  return n === 0 ? "No Terrain Tiles" : `${n} Terrain ${n === 1 ? "Tile" : "Tiles"}`;
}

export function locationWindowLabel(window: string): string {
  const map: Record<string, string> = { "1h": "1 Hour", "6h": "6 Hours", "12h": "12 Hours", "24h": "24 Hours", "7d": "7 Days" };
  return map[window] ?? window;
}

// Header tooltips: the setting keys behind each figure (Settings > Audit > Location).
export const LOCATION_TIPS = {
  linksChecked: "Settings: audit.location_min_links",
  failed: "Settings: audit.location_blame_share_pct, audit.location_yellow_share_pct",
  worstLink: "Settings: position_estimate.max_plausible_link_km",
  offEstimate: "Settings: audit.location_estimate_offset_limit_km, audit.location_offset_min_neighbours",
  clearance: "Settings: audit.location_terrain_percentile, audit.location_terrain_limit_margin, audit.location_earth_radius_factor",
  light: "Settings: audit.location_blame_share_pct, audit.location_yellow_share_pct, audit.location_min_links",
  reason: "Settings: audit.location_blame_share_pct, audit.location_estimate_offset_limit_km, audit.null_island_epsilon_deg",
} as const;
